// The chart's live indicator tails (moved from format.ts so it is strict and unit-tested).
//
// Recomputing emaArr/vwapBands over the full candle array on every trade print is
// O(bars) per tick and dominated main-thread time on a fast tape. makeTailComputer
// folds all CLOSED bars into prefix state — refreshed only when a bar closes — so
// each tick inside the forming bar prices the tail in O(1). Values are identical
// to the full recompute: same recurrences, same accumulation order, same rounding.
export type TailCandle = { time: number; open?: number; high: number; low: number; close: number; value: number };
export type EmaTail = { time: number; value: number };
export type BandTail = { time: number; vwap: number; u1: number; l1: number; u2: number; l2: number };

// Session VWAP anchors per ET trading day (shared with format.ts vwapBands).
let _etDayFmt: Intl.DateTimeFormat | null = null;
const _etDayCache = new Map<number, string>();
export function etTradingDay(timeSec: number): string {
  let d = _etDayCache.get(timeSec);
  if (d !== undefined) return d;
  if (!_etDayFmt) _etDayFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
  d = _etDayFmt.format(new Date(timeSec * 1000));
  _etDayCache.set(timeSec, d);
  return d;
}

/* `seed` = the server's newest indicator row ({ time of a closed bar, values }): the EMAs start from the server's
   value there and roll the server's own recurrence (screener-indicator-engine EmaColumn: price·α + prev·(1−α),
   α = 2/(N+1)) over the closed bars after it, so the live tail continues the server line instead of re-seeding from
   the first loaded bar — no jump where the server values end (the "EMA seam", mv5-prod/05 §2). When the last candle
   IS the seed's bar, its tail is the server's value itself. Without a seed (or a key the server has no value for)
   the window's first close seeds it, as before. */
export function makeTailComputer(emaPeriods: Record<string, number>, seed?: { time: number; values: Record<string, any> } | null) {
  const keys = Object.keys(emaPeriods);
  const alpha = (key: string): number => 2 / ((emaPeriods[key] ?? 1) + 1);
  let closed = -1; // closed-bar count folded into the prefix (= candles.length - 1)
  const emaPrev: Record<string, number | null> = {};
  let pv = 0, vv = 0, p2v = 0, day = '';

  function fold(candles: TailCandle[]) {
    closed = candles.length - 1;
    let si = -1;
    if (seed) {
      for (let i = closed - 1; i >= 0; i--) {
        const t = candles[i]!.time;
        if (t === seed.time) { si = i; break; }
        if (t < seed.time) break;
      }
    }
    for (const key of keys) {
      const k = alpha(key);
      const sv = si >= 0 && seed ? Number(seed.values?.[key]) : NaN;
      let prev: number | null = Number.isFinite(sv) ? sv : null;
      for (let i = prev == null ? 0 : si + 1; i < closed; i++) {
        const close = candles[i]!.close;
        prev = prev == null ? close : close * k + prev * (1 - k);
      }
      emaPrev[key] = prev;
    }
    pv = 0; vv = 0; p2v = 0; day = '';
    for (let i = 0; i < closed; i++) {
      const c = candles[i]!;
      const d = etTradingDay(c.time);
      if (d !== day) { pv = 0; vv = 0; p2v = 0; day = d; }
      const tp = (c.high + c.low + c.close) / 3;
      const vol = c.value || 0;
      pv += tp * vol; vv += vol; p2v += tp * tp * vol;
    }
  }

  return function tails(candles: TailCandle[], k1 = 1, k2 = 2): { ema: Record<string, EmaTail>; band: BandTail } | null {
    const n = candles.length;
    if (!n) return null;
    if (n - 1 !== closed) fold(candles); // a bar closed (or first call) → refold prefix
    const f = candles[n - 1]!;

    const ema: Record<string, EmaTail> = {};
    for (const key of keys) {
      const k = alpha(key);
      const prev = emaPrev[key] ?? null;
      const sv = seed && f.time === seed.time ? Number(seed.values?.[key]) : NaN;
      const v = Number.isFinite(sv) ? sv : prev == null ? f.close : f.close * k + prev * (1 - k);
      ema[key] = { time: f.time, value: +v.toFixed(4) };
    }

    let fpv = pv, fvv = vv, fp2v = p2v;
    if (etTradingDay(f.time) !== day) { fpv = 0; fvv = 0; fp2v = 0; } // forming bar opens a new session
    const tp = (f.high + f.low + f.close) / 3;
    const vol = f.value || 0;
    fpv += tp * vol; fvv += vol; fp2v += tp * tp * vol;
    const mean = fvv > 0 ? fpv / fvv : tp;
    const sd = Math.sqrt(fvv > 0 ? Math.max(0, fp2v / fvv - mean * mean) : 0);
    const band: BandTail = {
      time: f.time,
      vwap: +mean.toFixed(4),
      u1: +(mean + k1 * sd).toFixed(4), l1: +(mean - k1 * sd).toFixed(4),
      u2: +(mean + k2 * sd).toFixed(4), l2: +(mean - k2 * sd).toFixed(4),
    };
    return { ema, band };
  };
}
